import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fmtINRFull, fmtINR, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileEdit, Users as UsersIcon, Save, X } from "lucide-react";

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [proj, setProj] = useState(null);
  const [summary, setSummary] = useState(null);
  const [indents, setIndents] = useState([]);
  const [versions, setVersions] = useState([]);
  const [users, setUsers] = useState([]);
  const canEditBOQ = user?.role === "admin" || user?.role === "budget_controller";
  const canManageTeam = user?.role === "admin" || user?.role === "budget_controller";

  const load = async () => {
    const [p, s, ind, v] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/projects/${id}/summary`),
      api.get(`/indents`, { params: { project_id: id } }),
      api.get(`/projects/${id}/boq/versions`),
    ]);
    setProj(p.data); setSummary(s.data); setIndents(ind.data || []); setVersions(v.data || []);
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (canManageTeam) api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, [canManageTeam]);

  if (!proj) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0D3B66]">
          <ArrowLeft className="h-4 w-4" /> Back to projects
        </Link>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">{proj.name}</h1>
              <StatusChip status={proj.status} />
            </div>
            <p className="mt-1.5 text-slate-500">{proj.location} · Started {proj.start_date || "—"}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/projects/${id}/boq`}>
              <Button data-testid="open-boq-btn" className="rounded-full bg-[#0D3B66] hover:bg-[#114B82] text-white"><FileEdit className="h-4 w-4 mr-1.5" /> Open BOQ Editor</Button>
            </Link>
            {(user?.role === "budget_controller" || user?.role === "engineering_head") && (
              <Link to={`/projects/${id}/rate-analysis`}>
                <Button data-testid="open-ra-editor-btn" variant="outline" className="rounded-full border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white">
                  <FileEdit className="h-4 w-4 mr-1.5" /> Rate Analysis
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Metric label="Total Budget" value={fmtINR(summary?.total_budget)} testId="metric-budget" />
        <Metric label="BOQ Version" value={`v${summary?.budget_version || 0}`} sub={summary?.budget_status} testId="metric-version" />
        <Metric label="Approved Indents" value={fmtINR(summary?.indents_approved_value)} sub={`${summary?.indents_count || 0} raised`} testId="metric-approved" />
        <Metric label="Consumption" value={`${(summary?.consumption_pct || 0).toFixed(1)}%`} testId="metric-consumption" />
      </div>

      <section>
        <h3 className="text-sm font-semibold tracking-widest uppercase text-slate-500 mb-3">Recent indents</h3>
        {indents.length === 0 ? (
          <div className="text-sm text-slate-500 rounded-lg border border-dashed border-slate-200 p-8 text-center">No indents yet.</div>
        ) : (
          <div className="space-y-3">
            {indents.slice(0, 8).map((i) => (
              <Card key={i.id} className="p-4 border-slate-200">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">{i.iow_code}</span>
                      <span className="text-sm font-semibold text-slate-800">{i.description}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1.5">Qty {i.qty} {i.uom} · by {i.raised_by_name} · {fmtDate(i.raised_at)}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <div className="text-sm font-semibold text-[#0D3B66]">{fmtINR(i.value)}</div>
                    <StatusChip status={i.status} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold tracking-widest uppercase text-slate-500 mb-3">BOQ Version history</h3>
        <div className="space-y-2">
          {versions.map((v) => (
            <div key={v.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-[#0D3B66]">v{v.version}</span>
                <StatusChip status={v.status} />
                <span className="text-xs text-slate-500">{v.lines?.length || 0} lines · {fmtINR(v.total_amount)}</span>
              </div>
              <div className="text-xs text-slate-500">{fmtDate(v.updated_at || v.created_at)}</div>
            </div>
          ))}
        </div>
      </section>

      {canManageTeam && (
        <TeamAssignment proj={proj} users={users} onSaved={load} />
      )}
    </div>
  );
}

function TeamAssignment({ proj, users, onSaved }) {
  const [bc, setBc] = useState(proj.budget_controller_id || "");
  const [eh, setEh] = useState(proj.engineering_head_id || "");
  const [ses, setSes] = useState(proj.site_engineer_ids || []);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setBc(proj.budget_controller_id || "");
    setEh(proj.engineering_head_id || "");
    setSes(proj.site_engineer_ids || []);
  }, [proj]);

  const bcs = users.filter((u) => u.role === "budget_controller");
  const ehs = users.filter((u) => u.role === "engineering_head");
  const availableSE = users.filter((u) => u.role === "site_engineer");

  const toggleSE = (uid) => {
    setSes((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };
  const removeSE = (uid) => setSes((prev) => prev.filter((x) => x !== uid));

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.patch(`/projects/${proj.id}/assignments`, {
        budget_controller_id: bc || null,
        engineering_head_id: eh || null,
        site_engineer_ids: ses,
      });
      setMsg("Team updated.");
      onSaved();
    } catch (e) {
      setMsg(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const dirty =
    bc !== (proj.budget_controller_id || "") ||
    eh !== (proj.engineering_head_id || "") ||
    JSON.stringify([...ses].sort()) !== JSON.stringify([...(proj.site_engineer_ids || [])].sort());

  const seNameMap = Object.fromEntries(availableSE.map((u) => [u.id, u]));

  return (
    <section data-testid="team-assignment-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-sm font-semibold tracking-widest uppercase text-slate-500 flex items-center gap-2">
          <UsersIcon className="h-4 w-4" /> Team
        </h3>
        {dirty && (
          <Button data-testid="team-save-btn" onClick={save} disabled={saving} className="rounded-full bg-[#0D3B66] hover:bg-[#114B82]">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        )}
      </div>
      {msg && <div data-testid="team-msg" className="text-sm text-slate-600 mb-3">{msg}</div>}
      <Card className="p-5 border-slate-200 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Budget Controller</div>
            <Select value={bc || "__none"} onValueChange={(v) => setBc(v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="team-bc-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— Unassigned —</SelectItem>
                {bcs.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Engineering Head</div>
            <Select value={eh || "__none"} onValueChange={(v) => setEh(v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="team-eh-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— Unassigned —</SelectItem>
                {ehs.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Site Engineers ({ses.length})</div>

          {ses.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="team-se-chips">
              {ses.map((uid) => {
                const u = seNameMap[uid];
                if (!u) return null;
                return (
                  <span
                    key={uid}
                    data-testid={`team-se-chip-${uid}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0D3B66]/10 text-[#0D3B66] px-3 py-1 text-xs font-medium"
                  >
                    {u.name}
                    <button
                      data-testid={`team-se-remove-${uid}`}
                      onClick={() => removeSE(uid)}
                      className="hover:text-rose-600"
                      title="Remove"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          <div className="pt-2">
            <div className="text-[11px] text-slate-500 mb-1.5">Add site engineers:</div>
            <div className="flex flex-wrap gap-2">
              {availableSE.filter((u) => !ses.includes(u.id)).map((u) => (
                <button
                  key={u.id}
                  data-testid={`team-se-add-${u.id}`}
                  onClick={() => toggleSE(u.id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 hover:border-[#F97316] hover:bg-orange-50/40 px-3 py-1 text-xs text-slate-600"
                >
                  <span>+</span> {u.name}
                </button>
              ))}
              {availableSE.filter((u) => !ses.includes(u.id)).length === 0 && (
                <div className="text-xs text-slate-400">All site engineers already assigned.</div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </section>
  );
}

function Metric({ label, value, sub, testId }) {
  return (
    <Card data-testid={testId} className="p-5 border-slate-200 shadow-none">
      <div className="text-xs font-semibold tracking-widest uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-[#0D3B66] tracking-tight">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </Card>
  );
}
